import { useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useAppDispatch } from 'utilities/hooks';
import { addSnackbar, SnackbarTypes } from '../slices/snackbarsSlice';
import { moveCategory } from 'slices/categoriesActions';
import { Category, Document } from 'interfaces/documents';
import { moveDocument } from 'slices/documentsActions';
import { isCategory } from 'utilities/constants';
import { useModal } from './useModal';

export interface DragDropConfig {
    canDrag?: boolean;
    // The folders on both sides of the move. The synthetic root node carries no
    // metadata, so its id arrives undefined and means the root folder.
    onMove?: (affectedFolderIds: Array<number | null | undefined>) => void;
    canDrop?: (droppingNode: Category | Document) => boolean;
}

export interface DragDropReturn {
    active: boolean;
    drag: (element: HTMLElement | HTMLDivElement | null) => void;
    drop: (element: HTMLElement | HTMLDivElement | null) => void;
}

export const useDocumentsDragAndDrop = (
    item: Category | Document | null,
    config: DragDropConfig = {}
): DragDropReturn => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const [canDrag, setCanDrag] = useState<boolean>(config.canDrag ?? true);
    const [active, setActive] = useState<boolean>(false);
    const componentItem = isCategory(item) ? (item as Category) : (item as Document);

    useEffect(() => {
        if (isCategory(item)) {
            setCanDrag(componentItem.id !== 0 && !(componentItem as Category).is_locked);
        } else {
            setCanDrag(config.canDrag ?? true);
        }
    }, [item, config.canDrag]);

    const [_, drag] = useDrag({
        item: componentItem,
        type: 'node',
        canDrag: () => canDrag,
    });

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: 'node',
        canDrop: (droppingNode: Category | Document) => {
            if (config.canDrop) {
                return config.canDrop(droppingNode);
            } else {
                if (!isCategory(componentItem)) return false;

                const dropZoneNode = componentItem as Category;
                // Prevent dropping on self
                if (dropZoneNode.id === droppingNode.id) return false;
                return true;
            }
        },
        drop: async (droppingNode: Category | Document) => {
            if (!isOver || !canDrop) return;
            const movePromises: Promise<any>[] = [];

            // We can only drop into a category
            if (!isCategory(componentItem)) return;
            const dropZoneNode = componentItem as Category;
            if (!isCategory(dropZoneNode)) return;

            const parentId = isCategory(droppingNode) ? droppingNode.parent_id : droppingNode.category_id;
            // Prevent dropping into parent
            if (dropZoneNode?.id === parentId) return;
            openModal('confirmationModal', {
                title: 'Move Item',
                text: `Are you sure you want to move this item into ${dropZoneNode.name ? `"${dropZoneNode.name}"` : 'the root folder'}?`,
                confirmLabel: 'Move',
                cancelLabel: 'Cancel'
            }, {
                onConfirm: async () => {
                    if (droppingNode.type === 'category') {
                        movePromises.push(dispatch(moveCategory({
                            suppressError: true,
                            id: (droppingNode as Category).uuid,
                            parent_id: dropZoneNode?.id || null,
                        })));
                    } else {
                        movePromises.push(dispatch(moveDocument({
                            suppressError: true,
                            id: (droppingNode as Document).id,
                            category_id: dropZoneNode?.id || null,
                        })));
                    }

                    if (movePromises.length) {
                        const results = await Promise.all(movePromises);
                        const failedResults = results.filter(result => result.meta.requestStatus === 'rejected');
                        const errorMessages = failedResults.map(result => result.payload?.data?.message);

                        if (failedResults.length) {
                            errorMessages.forEach(message => {
                                dispatch(addSnackbar({
                                    type: SnackbarTypes.ERROR,
                                    message: message,
                                }));
                            });
                        } else {
                            dispatch(addSnackbar({
                                type: SnackbarTypes.SUCCESS,
                                message: 'Item moved successfully',
                            }));
        
                            config.onMove?.([parentId, dropZoneNode.id]);
                        }
                    }
                },
            });
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
        hover: (_, monitor) => {
            if (monitor.canDrop()) {
                setActive(true);
            }
        },
    });

    useEffect(() => {
        setActive(false);
    }, [isOver]);

    return {
        active,
        drag,
        drop,
    };
};
